import { useNavigate } from 'react-router-dom';
import { Briefcase, Phone, Star } from 'lucide-react';

const COLUMNS = [
  { id: 'sourced',             label: 'Sourced',           color: 'bg-slate-100 text-slate-600 border-slate-200',     dot: 'bg-slate-400' },
  { id: 'submitted',           label: 'Submitted',         color: 'bg-blue-100 text-blue-700 border-blue-200',        dot: 'bg-blue-500' },
  { id: 'client_review',       label: 'Client Review',     color: 'bg-purple-100 text-purple-700 border-purple-200',  dot: 'bg-purple-500' },
  { id: 'interview_scheduled', label: 'Interview',         color: 'bg-amber-100 text-amber-700 border-amber-200',     dot: 'bg-amber-500' },
  { id: 'offer_extended',      label: 'Offer Extended',    color: 'bg-orange-100 text-orange-700 border-orange-200',  dot: 'bg-orange-500' },
  { id: 'placed',              label: 'Placed ✓',          color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  { id: 'rejected',            label: 'Rejected',          color: 'bg-red-100 text-red-600 border-red-200',           dot: 'bg-red-400' },
];

// Map raw status → column id
const toColumnId = (status) => {
  const s = (status || 'sourced').toLowerCase();
  const valid = ['sourced','submitted','client_review','interview_scheduled','offer_extended','placed','rejected','withdrawn','offer_declined'];
  if (valid.includes(s)) return s;
  if (['pending','scored','shortlisted','screened'].includes(s)) return 'sourced';
  return 'sourced';
};

function KanbanSkeleton() {
  return (
    <div className="flex gap-6 overflow-x-auto pb-4 min-h-[600px]">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="min-w-[300px] w-[300px] bg-slate-50/50 rounded-2xl p-4 animate-pulse">
          <div className="h-6 w-32 bg-slate-200 rounded-lg mb-4" />
          <div className="space-y-3">
            {[...Array(3)].map((_, j) => (
              <div key={j} className="h-28 bg-white rounded-xl shadow-sm" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CandidateKanbanBoard({ candidates, loading, onCandidateClick, onSubmitCandidate, pipelineCounts = {} }) {
  const navigate = useNavigate();

  const handleClick = (c) => {
    if (onCandidateClick) {
      onCandidateClick(c);
    } else {
      navigate(`/candidates/${c.candidate_id || c.id}`);
    }
  };

  if (loading) return <KanbanSkeleton />;

  return (
    <div>
    <div className="flex gap-5 overflow-x-auto pb-6 items-start min-h-[600px] px-8 py-5">
      {COLUMNS.map(col => {
        const colCandidates = candidates.filter(c => toColumnId(c.status) === col.id);

        return (
          <div
            key={col.id}
            data-testid={`kanban-column-${col.id}`}
            className="min-w-[300px] w-[300px] bg-slate-50/50 border border-slate-200/50 rounded-2xl p-4 flex flex-col"
            style={{ maxHeight: 'calc(100vh - 220px)' }}
          >
            {/* Column header */}
            <div className="flex items-center justify-between mb-4 px-1">
              <h3 className="font-bold text-slate-800 text-sm">{col.label}</h3>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${col.color}`}>
                {col.id === 'sourced'
                  ? colCandidates.length
                  : (pipelineCounts[col.id] ?? colCandidates.length)
                }
              </span>
            </div>

            {/* Cards */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-0.5">
              {colCandidates.map(c => {
                const sd = c.sourced_data || {};
                const score = c.linkedin_score?.score
                  ?? c.relevance_score
                  ?? (c.score != null ? Math.round(c.score * 10) : null);
                const title   = sd.current_title   || c.job_title || '';
                const company = sd.current_company || '';
                const contact = c.captured_email || c.email || c.phone || '';

                return (
                  <div
                    key={c.candidate_id || c.id}
                    data-testid={`kanban-card-${c.candidate_id}`}
                    onClick={() => handleClick(c)}
                    className="bg-white/90 backdrop-blur-sm border border-slate-200/70 rounded-xl p-4
                               shadow-sm hover:shadow-xl hover:-translate-y-0.5 hover:border-blue-300/60
                               transition-all duration-200 cursor-pointer group"
                  >
                    {/* Name + score */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors leading-snug">
                        {c.name || 'Unknown Candidate'}
                      </h4>
                      {score != null && (
                        <span className={`flex-shrink-0 flex items-center gap-0.5 px-2 py-0.5 rounded-lg text-[11px] font-bold border ${
                          score >= 80 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                          score >= 60 ? 'bg-amber-50 border-amber-200 text-amber-700' :
                                        'bg-red-50 border-red-200 text-red-700'
                        }`}>
                          <Star className="w-2.5 h-2.5" /> {score}
                        </span>
                      )}
                    </div>

                    {/* Meta */}
                    <div className="space-y-1.5 mt-2">
                      {(title || company) && (
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                          <Briefcase className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{[title, company].filter(Boolean).join(' · ')}</span>
                        </div>
                      )}
                      {contact && (
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                          <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{contact}</span>
                        </div>
                      )}
                    </div>

                    {/* Open to work badge */}
                    {c.open_to_work && (
                      <span className="inline-flex items-center gap-0.5 mt-2 px-1.5 py-0.5 rounded-full
                                       text-[9px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-300">
                        🟢 Open to Work
                      </span>
                    )}

                    {/* Submit to Client button - only for sourced column */}
                    {col.id === 'sourced' && onSubmitCandidate && (
                      <button
                        data-testid={`kanban-submit-btn-${c.candidate_id || c.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSubmitCandidate(c);
                        }}
                        className="mt-3 text-xs bg-blue-600 text-white px-2 py-1 rounded-lg hover:bg-blue-700"
                      >
                        Submit →
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Empty column */}
              {colCandidates.length === 0 && (
                <div className="text-center py-10 px-4 border-2 border-dashed border-slate-200 rounded-xl">
                  <p className="text-sm text-slate-400 font-medium">No candidates here</p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
    <p className="text-xs text-slate-400 text-center mt-2">← Scroll to see all stages →</p>
    </div>
  );
}
